<script lang="ts">
  import { tick } from 'svelte';
  import { coarsePointer, isCoarsePointer, viewport } from '../lib/stores/viewport';
  import { router, page } from '@inertiajs/svelte';
  import type { Project, ProjectCategory, ProjectStatus } from '../lib/types';
  import {
    projectModalOpen,
    projectModalProject,
    projectModalContext,
    closeProjectModal,
    notifyProjectUpdated,
    type ProjectModalContext,
  } from '../lib/stores/ui';
  import { undoableSoftDelete } from '../lib/undoManager';
  import { jsonHeaders } from '../lib/apiHelpers';
  import { toast } from 'svelte-sonner';
  import { useModalAnimation } from '../lib/useModalAnimation.svelte';
  import TagPicker from './TagPicker.svelte';
  import ResponsiveOverlay from './ResponsiveOverlay.svelte';

  const modal = useModalAnimation(() => $projectModalOpen, closeProjectModal);
  import { MS_PER_DAY, parseCalendarDate, formatCalendarDate, toDateStr } from '../lib/utils';

  let title = $state('');
  let category = $state<ProjectCategory>('other');
  let status = $state<ProjectStatus>('planned');
  let notes = $state('');
  let isPriority = $state(false);
  let isSubmitting = $state(false);
  let durationWeeks = $state(1);
  let startDate = $state('');
  let endDate = $state('');
  let schedulingState = $state<'backlog' | 'scheduled'>('backlog');
  let showRemoveScheduleTooltip = $state(false);
  let selectedTags = $state<string[]>([]);

  let titleInput: HTMLInputElement | undefined = $state();
  let cardEl: HTMLDivElement | undefined = $state();

  const categories: { value: ProjectCategory; label: string; icon: string }[] = [
    { value: 'tech', label: 'Tech', icon: '💻' },
    { value: 'health', label: 'Health', icon: '🏃' },
    { value: 'personal', label: 'Personal', icon: '👤' },
    { value: 'finance', label: 'Finance', icon: '💰' },
    { value: 'travel', label: 'Travel', icon: '✈️' },
    { value: 'other', label: 'Other', icon: '📋' },
  ];

  const statuses: { value: ProjectStatus; label: string; icon: string }[] = [
    { value: 'planned', label: 'Planned', icon: '📋' },
    { value: 'active', label: 'Active', icon: '🔄' },
    { value: 'done', label: 'Done', icon: '✅' },
  ];

  let isEditing = $derived(!!$projectModalProject);
  let scheduleOnly = $derived($projectModalContext?.mode === 'schedule');
  let modalTitle = $derived(
    scheduleOnly ? 'Project Schedule' : isEditing ? 'Edit Project' : 'New Project',
  );

  // Snapshot of initial form values for dirty-state detection
  let initialValues = $state({
    title: '',
    category: 'other' as ProjectCategory,
    status: 'planned' as ProjectStatus,
    notes: '',
    isPriority: false,
    durationWeeks: 1,
    startDate: '',
    schedulingState: 'backlog' as 'backlog' | 'scheduled',
    tagNames: [] as string[],
  });

  let isDirty = $derived(
    title !== initialValues.title ||
      category !== initialValues.category ||
      status !== initialValues.status ||
      notes !== initialValues.notes ||
      isPriority !== initialValues.isPriority ||
      durationWeeks !== initialValues.durationWeeks ||
      startDate !== initialValues.startDate ||
      schedulingState !== initialValues.schedulingState ||
      selectedTags.join('\u0000') !== initialValues.tagNames.join('\u0000'),
  );

  // Initialize form when modal opens
  // eslint-disable-next-line momentium/no-unguarded-effect -- form init lifecycle: must reset all form fields when modal opens and populate from store state
  $effect(() => {
    if ($projectModalOpen) {
      const project = $projectModalProject;
      const context = $projectModalContext;

      if (project) {
        // Editing existing project
        title = project.title;
        category = project.category;
        status = project.status;
        notes = project.notes || '';
        isPriority = project.isPriority;
        selectedTags = (project.tags ?? []).map((t) => t.name);
        startDate = project.startDate || '';
        endDate = project.endDate || '';
        schedulingState = project.schedulingState;
        // Calculate duration from dates (use UTC to avoid DST issues)
        let initDuration = 1;
        if (project.startDate && project.endDate) {
          const days = Math.round(
            (parseCalendarDate(project.endDate).getTime() -
              parseCalendarDate(project.startDate).getTime()) /
              MS_PER_DAY,
          );
          initDuration = Math.max(1, Math.ceil(days / 7));
        }
        durationWeeks = initDuration;

        // Snapshot initial values from project source (not $state) for dirty detection
        initialValues = {
          title: project.title,
          category: project.category,
          status: project.status,
          notes: project.notes || '',
          isPriority: project.isPriority,
          durationWeeks: initDuration,
          startDate: project.startDate || '',
          schedulingState: project.schedulingState,
          tagNames: (project.tags ?? []).map((t) => t.name),
        };
      } else {
        // Creating new project
        const initStatus = context?.status || 'planned';
        const initDuration = context?.durationWeeks || 1;
        const initStartDate = getInitialCreateStartDate(context);
        const initTitle = context?.initialTitle || '';
        const initNotes = context?.initialNotes || '';
        title = initTitle;
        category = 'other';
        status = initStatus;
        notes = initNotes;
        isPriority = false;
        selectedTags = [];
        durationWeeks = initDuration;
        startDate = initStartDate;
        endDate = '';
        schedulingState = context?.schedulingState || 'backlog';

        // Snapshot initial values from defaults (not $state) for dirty detection
        initialValues = {
          title: initTitle,
          category: 'other',
          status: initStatus,
          notes: initNotes,
          isPriority: false,
          durationWeeks: initDuration,
          startDate: initStartDate,
          schedulingState: context?.schedulingState || 'backlog',
          tagNames: [],
        };
      }

      isSubmitting = false;

      // Focus title input after DOM updates. On touch, focus the dialog instead so
      // screen readers start inside it without the keyboard covering the form.
      tick().then(() => {
        if (isCoarsePointer()) {
          cardEl?.focus();
        } else if (!scheduleOnly && titleInput) {
          titleInput.focus();
          // Move cursor to end of text
          titleInput.setSelectionRange(titleInput.value.length, titleInput.value.length);
        }
      });
    }
  });

  function alignToSunday(dateStr: string): string {
    if (!dateStr) return '';
    const date = parseCalendarDate(dateStr);
    date.setUTCDate(date.getUTCDate() - date.getUTCDay());
    return toDateStr(date);
  }

  function getInitialCreateStartDate(context: ProjectModalContext | null): string {
    if (context?.schedulingState !== 'scheduled') return '';
    const date = context.targetDate || (context.monthKey ? `${context.monthKey}-01` : '');
    return alignToSunday(date);
  }

  function computeEndDate(): string {
    if (!startDate) return '';
    const end = parseCalendarDate(startDate);
    end.setUTCDate(end.getUTCDate() + (durationWeeks * 7 - 1));
    return toDateStr(end);
  }

  function handleStartDateChange(e: Event) {
    const value = (e.currentTarget as HTMLInputElement).value;
    startDate = alignToSunday(value);
    endDate = computeEndDate();
    schedulingState = startDate ? 'scheduled' : 'backlog';
  }

  function clearSchedule() {
    startDate = '';
    endDate = '';
    schedulingState = 'backlog';
  }

  function shiftStartDate(weekOffset: number) {
    if (!startDate) return;
    const shifted = parseCalendarDate(startDate);
    shifted.setUTCDate(shifted.getUTCDate() + weekOffset * 7);
    startDate = toDateStr(shifted);
    endDate = computeEndDate();
  }

  function handleSubmit(e: Event) {
    e.preventDefault();
    if ((!scheduleOnly && !title.trim()) || isSubmitting) return;

    startDate = alignToSunday(startDate);
    endDate = computeEndDate();
    schedulingState = startDate ? 'scheduled' : 'backlog';
    const context = $projectModalContext;
    const project = $projectModalProject;

    isSubmitting = true;

    if (isEditing && project) {
      const updateData: Record<string, any> = {
        scheduling_state: schedulingState,
      };

      if (!scheduleOnly) {
        updateData.title = title.trim();
        updateData.category = category;
        updateData.status = status;
        updateData.notes = notes.trim() || null;
        updateData.is_priority = isPriority;
        updateData.tag_names = selectedTags;
      }

      if (startDate) {
        updateData.start_date = startDate;
        updateData.end_date = endDate;
        updateData.target_date = startDate;
      } else {
        updateData.start_date = null;
        updateData.end_date = null;
        updateData.target_date = null;
        updateData.month_key = null;
      }
      router.patch(
        `/projects/${project.id}`,
        {
          project: updateData,
        },
        {
          preserveScroll: true,
          onSuccess: () => {
            notifyProjectUpdated({
              ...project,
              title: title.trim(),
              category,
              status,
              notes: notes.trim() || undefined,
              isPriority,
              schedulingState,
            });
            closeProjectModal();
          },
          onError: (errors) => {
            isSubmitting = false;
            toast.error(firstInertiaError(errors) ?? 'Could not save project');
          },
        },
      );
    } else if (schedulingState === 'backlog') {
      // Add to backlog
      router.post(
        '/projects/quick_add_backlog',
        {
          title: title.trim(),
          category,
          notes: notes.trim() || null,
          tag_names: selectedTags,
        },
        {
          preserveScroll: true,
          onSuccess: () => {
            context?.onCreated?.();
            closeProjectModal();
          },
          onError: (errors) => {
            isSubmitting = false;
            toast.error(firstInertiaError(errors) ?? 'Could not create project');
          },
        },
      );
    } else if (context?.monthKey) {
      // Add to month (with optional target_date for week-specific placement)
      router.post(
        '/projects/quick_add_month',
        {
          title: title.trim(),
          category,
          month_key: context.monthKey,
          target_date: startDate || context.targetDate || null,
          status: context.status || 'planned',
          duration_weeks: durationWeeks,
          tag_names: selectedTags,
        },
        {
          preserveScroll: true,
          onSuccess: () => {
            context?.onCreated?.();
            closeProjectModal();
          },
          onError: (errors) => {
            isSubmitting = false;
            toast.error(firstInertiaError(errors) ?? 'Could not create project');
          },
        },
      );
    }
  }

  function firstInertiaError(errors: Record<string, unknown> | undefined): string | null {
    if (!errors) return null;
    const first = Object.values(errors).flat().find(Boolean);
    return typeof first === 'string' && first.trim() ? first : null;
  }

  function requestClose() {
    if (!isDirty || confirm('You have unsaved changes. Discard them?')) {
      // The mobile sheet slides itself out; the desktop modal animates first.
      if ($viewport.isMobile) closeProjectModal();
      else modal.animateClose();
    }
  }

  function handleDelete() {
    const project = $projectModalProject;
    if (!project) return;

    const projectId = project.id;
    // Detect if user is on this project's show page
    const currentUrl = $page.url;
    const isOnProjectPage = currentUrl.includes(`/projects/${projectId}`);

    // Close modal immediately
    closeProjectModal();

    const projectPreview =
      project.title.length > 40 ? project.title.slice(0, 40) + '…' : project.title;

    undoableSoftDelete({
      message: `Deleted "${projectPreview}"`,
      onOptimisticRemove: () => {
        // No immediate UI change for projects — Inertia props are refreshed
        // after the server soft-deletes in onDelete below.
      },
      onOptimisticRestore: () => {
        // No immediate UI change — the restore is handled in onRestore
        // by reloading from the server.
      },
      onDelete: async () => {
        const response = await fetch(`/projects/${projectId}`, {
          method: 'DELETE',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error('Server error');
        // Now that the server has soft-deleted, refresh the UI
        if (isOnProjectPage) {
          router.visit('/projects');
        } else {
          router.reload();
        }
      },
      onRestore: async () => {
        const response = await fetch(`/projects/${projectId}/undiscard`, {
          method: 'POST',
          headers: jsonHeaders(),
        });
        if (!response.ok) throw new Error('Server error');
        router.reload();
      },
    });
  }

  function handleKeydown(e: KeyboardEvent) {
    // On mobile the bottom sheet handles Esc itself (and calls requestClose).
    if (e.key === 'Escape' && !$viewport.isMobile) {
      requestClose();
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- Desktop: centered modal. Mobile: full-height bottom sheet, actions pinned at the bottom. -->
<ResponsiveOverlay
  open={$projectModalOpen}
  onClose={requestClose}
  detents={['full']}
  title={modalTitle}
  initialFocus={scheduleOnly ? 'container' : undefined}
>
  {#snippet desktop(content)}
    {#if $projectModalOpen}
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class="modal-overlay"
        class:closing={modal.closing}
        bind:this={modal.overlayEl}
        onclick={requestClose}
        role="presentation"
      >
        <div
          class="modal-card"
          bind:this={cardEl}
          onclick={(e) => e.stopPropagation()}
          role="dialog"
          tabindex="-1"
        >
          <!-- Header -->
          <div class="flex items-center justify-between border-b border-gray-200 px-5 py-4">
            <h2 class="text-lg font-semibold text-gray-900">{modalTitle}</h2>
            <button
              type="button"
              class="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              onclick={requestClose}
              aria-label="Close modal"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {@render content()}
        </div>
      </div>
    {/if}
  {/snippet}

  <!-- Form -->
  <form
    onsubmit={handleSubmit}
    class="project-form p-5"
    class:sheet={$viewport.isMobile}
    data-sheet-fill
    novalidate
  >
    <div class="form-fields">
      {#if !scheduleOnly}
        <!-- Title -->
        <div class="mb-3">
          <label for="project-title" class="mb-1 block text-sm font-medium text-gray-700">
            Title
          </label>
          <input
            autocomplete="off"
            id="project-title"
            type="text"
            bind:this={titleInput}
            bind:value={title}
            data-sheet-autofocus
            placeholder="Project title..."
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <!-- Category -->
        <div class="mb-3" role="group" aria-labelledby="category-label">
          <span id="category-label" class="mb-1.5 block text-sm font-medium text-gray-700">
            Category
          </span>
          <div class="grid grid-cols-3 gap-1.5">
            {#each categories as cat}
              <button
                type="button"
                class="flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs transition-colors
                   {category === cat.value
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300'}"
                onclick={() => (category = cat.value)}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            {/each}
          </div>
        </div>

        <!-- Status (only for editing) -->
        {#if isEditing}
          <div class="mb-3" role="group" aria-labelledby="status-label">
            <span id="status-label" class="mb-1.5 block text-sm font-medium text-gray-700">
              Status
            </span>
            <div class="grid grid-cols-3 gap-1.5">
              {#each statuses as st}
                <button
                  type="button"
                  class="flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs transition-colors
                     {status === st.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'}"
                  onclick={() => (status = st.value)}
                >
                  <span>{st.icon}</span>
                  <span>{st.label}</span>
                </button>
              {/each}
            </div>
          </div>
        {/if}

        <!-- Flags -->
        <div class="mb-3 flex gap-4">
          <label class="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              bind:checked={isPriority}
              class="h-4 w-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
            />
            <span class="text-sm text-gray-700">⭐ Priority</span>
          </label>
        </div>
      {/if}

      <!-- Schedule -->
      {#if isEditing || $projectModalContext?.schedulingState === 'scheduled'}
        <div class="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-2.5">
          <div class="mb-2">
            <span class="block text-sm font-semibold text-gray-900">Schedule</span>
            {#if schedulingState === 'backlog'}
              <p class="mt-0.5 text-xs text-gray-500">
                This project is currently in the backlog. Add a start week to schedule it.
              </p>
            {:else}
              <p class="mt-0.5 text-xs text-gray-500">
                Adjust the date range, or remove the schedule to move it back to backlog.
              </p>
            {/if}
          </div>

          <div class="mb-2.5">
            <label for="project-start-week" class="mb-1 block text-sm font-medium text-gray-700">
              Start week
            </label>
            <div class="flex flex-wrap items-center gap-2">
              <input
                autocomplete="off"
                id="project-start-week"
                type="date"
                bind:value={startDate}
                onchange={handleStartDateChange}
                class="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                class="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                onclick={() => shiftStartDate(-1)}
                disabled={!startDate}
              >
                ← 1 week
              </button>
              <button
                type="button"
                class="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                onclick={() => shiftStartDate(1)}
                disabled={!startDate}
              >
                +1 week →
              </button>
            </div>
            <p class="mt-1 text-xs text-gray-500">
              Pick any date; projects start on the Sunday of that week.
            </p>
          </div>

          <div role="group" aria-labelledby="duration-label">
            <span id="duration-label" class="mb-2 block text-sm font-medium text-gray-700">
              Duration
            </span>
            <div class="flex flex-wrap gap-1.5">
              {#each [{ value: 1, label: '1 week' }, { value: 2, label: '2 weeks' }, { value: 3, label: '3 weeks' }, { value: 4, label: '4 weeks' }, { value: 6, label: '6 weeks' }, { value: 8, label: '8 weeks' }] as opt}
                <button
                  type="button"
                  class="rounded-md border bg-white px-2 py-1 text-xs transition-colors
                       {durationWeeks === opt.value
                    ? 'border-blue-500 bg-blue-50 font-medium text-blue-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'}"
                  onclick={() => {
                    durationWeeks = opt.value;
                    endDate = computeEndDate();
                  }}
                >
                  {opt.label}
                </button>
              {/each}
              <!-- Custom input -->
              <div class="flex items-center gap-1">
                <input
                  autocomplete="off"
                  type="number"
                  min="1"
                  max="52"
                  bind:value={durationWeeks}
                  oninput={() => (endDate = computeEndDate())}
                  class="w-12 rounded-md border border-gray-300 bg-white px-1.5 py-1 text-center text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
                <span class="text-xs text-gray-500">wk{durationWeeks !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>

          {#if startDate}
            {@const sd = parseCalendarDate(startDate)}
            {@const ed = parseCalendarDate(computeEndDate())}
            <div class="mt-2 flex items-center justify-between gap-3 text-xs text-gray-600">
              <p class="min-w-0">
                Range: <span class="font-medium text-gray-800"
                  >{formatCalendarDate(sd, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })} — {formatCalendarDate(ed, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}</span
                >
              </p>
              <div class="remove-schedule-wrapper shrink-0">
                <button
                  type="button"
                  class="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-gray-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                  onclick={clearSchedule}
                  onmouseenter={() => (showRemoveScheduleTooltip = true)}
                  onmouseleave={() => (showRemoveScheduleTooltip = false)}
                  onfocus={() => (showRemoveScheduleTooltip = true)}
                  onblur={() => (showRemoveScheduleTooltip = false)}
                >
                  Remove schedule
                </button>
                {#if showRemoveScheduleTooltip && !$coarsePointer}
                  <div class="remove-schedule-tooltip" role="tooltip">
                    <div class="tooltip-title">Move to backlog</div>
                    <div class="tooltip-desc">
                      Removes this date range and moves the project back to the backlog.
                    </div>
                  </div>
                {/if}
              </div>
            </div>
          {/if}
        </div>
      {/if}

      {#if !scheduleOnly}
        <!-- Notes -->
        <div class="mb-3">
          <label for="project-notes" class="mb-1 block text-sm font-medium text-gray-700">
            Notes <span class="font-normal text-gray-400">(optional)</span>
          </label>
          <textarea
            id="project-notes"
            bind:value={notes}
            placeholder="Additional details..."
            rows="3"
            class="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          ></textarea>
        </div>

        <!-- Tags -->
        <div class="mb-6">
          <span id="tags-label" class="mb-1 block text-sm font-medium text-gray-700">
            Tags <span class="font-normal text-gray-400">(optional)</span>
          </span>
          <TagPicker selected={selectedTags} onChange={(names) => (selectedTags = names)} />
        </div>
      {/if}
    </div>

    <!-- Actions -->
    <div class="form-actions flex justify-between">
      <div>
        {#if isEditing && !scheduleOnly}
          <button
            type="button"
            class="rounded-lg px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
            onclick={handleDelete}
            disabled={isSubmitting}
          >
            Delete
          </button>
        {/if}
      </div>
      <div class="flex gap-3">
        <button
          type="button"
          class="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
          onclick={requestClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isSubmitting || (!scheduleOnly && !title.trim())}
        >
          {isSubmitting
            ? 'Saving...'
            : scheduleOnly
              ? 'Save Schedule'
              : isEditing
                ? 'Save Changes'
                : 'Create Project'}
        </button>
      </div>
    </div>
  </form>
</ResponsiveOverlay>

<style>
  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    animation: overlay-in 150ms ease-out both;
  }

  .modal-overlay.closing {
    animation: overlay-out 100ms ease-in both;
  }

  .modal-overlay.closing .modal-card {
    animation: card-out 100ms ease-in both;
  }

  .modal-card {
    outline: none;
    background: white;
    border-radius: 0.75rem;
    box-shadow:
      0 20px 25px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 28rem;
    margin: 0 1rem;
    overflow: hidden;
    animation: card-in 150ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  .remove-schedule-wrapper {
    position: relative;
    display: inline-flex;
  }

  .remove-schedule-tooltip {
    position: absolute;
    right: calc(100% + 0.5rem);
    top: 50%;
    width: 14rem;
    padding: 0.5rem 0.625rem;
    background: #1f2937;
    color: white;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 100;
    pointer-events: none;
    transform: translateY(-50%);
    animation: tooltip-in-left 0.15s ease;
  }

  .tooltip-title {
    font-size: 0.6875rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .tooltip-desc {
    font-size: 0.625rem;
    line-height: 1.4;
    color: #d1d5db;
  }

  @keyframes tooltip-in-left {
    from {
      opacity: 0;
      transform: translateY(-50%) translateX(4px);
    }
    to {
      opacity: 1;
      transform: translateY(-50%) translateX(0);
    }
  }

  @keyframes overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes card-in {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes card-out {
    from {
      transform: scale(1);
      opacity: 1;
    }
    to {
      transform: scale(0.95);
      opacity: 0;
    }
  }

  /* Mobile bottom sheet: fields scroll, actions stay pinned above the safe area. */
  .project-form.sheet {
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  .project-form.sheet .form-fields {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 1rem;
  }

  .project-form.sheet .form-actions {
    flex-shrink: 0;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid #e5e7eb;
    background: white;
  }

  .project-form.sheet button,
  .project-form.sheet input:not([type='checkbox']) {
    min-height: var(--mobile-tap);
  }

  .project-form.sheet label:has(> input[type='checkbox']) {
    min-height: var(--mobile-tap);
  }
</style>
